import { isPrefixedKey } from '@nucleus/ncu-key-prefix';
import { unwrapResult } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { applyLinkParametersToFlow, fetchAccessRequestFlowConfig, fetchFlowConfig } from '@shared/actions/flows';
import { STATE_ERROR, STATE_LOADED, STATE_LOADING } from '@shared/constants/state';
import {
  selectFlowConfigByShortcode,
  selectFlowConfigLoadedAtByShortcode,
} from '@shared/reducers/flows/flowsByIdReducer';
import { Hub } from 'aws-amplify/utils';
import { pick as _pick } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const useFlowConfig = (flowShortcode, revision, linkParams, ownerId) => {
  const config = useSelector((state) => selectFlowConfigByShortcode(state, flowShortcode));
  const configLoadedAt = useSelector((state) => selectFlowConfigLoadedAtByShortcode(state, flowShortcode));
  const dispatch = useDispatch();

  const initialState = configLoadedAt < new Date() ? STATE_LOADED : STATE_LOADING;
  const [state, setState] = useState(initialState);

  const fetchConfig = useCallback(async () => {
    try {
      if (isPrefixedKey(undefined, flowShortcode) !== true) {
        await dispatch(fetchFlowConfig({ flowShortcode: flowShortcode, revision: revision })).then(unwrapResult);
      } else {
        await dispatch(
          fetchAccessRequestFlowConfig({
            ownerId: ownerId,
            flowShortcode: flowShortcode,
          })
        ).then(unwrapResult);
      }
    } catch (error) {
      setState(STATE_ERROR);
      return;
    }

    setState(STATE_LOADED);
  }, [dispatch, flowShortcode, revision, ownerId]);

  const registerAuthListeners = useCallback(() => {
    const listener = buildAuthListener(fetchConfig);
    const unsubscribe = Hub.listen('auth', listener);
    return unsubscribe;
  }, [fetchConfig]);

  useEffect(() => {
    fetchConfig();
    return registerAuthListeners();
  }, [fetchConfig, registerAuthListeners]);

  useEffect(() => {
    if (state === STATE_LOADED) {
      dispatch(applyLinkParametersToFlow(flowShortcode, linkParams));
    }
  }, [dispatch, flowShortcode, linkParams, state]);

  const reloadFlow = useCallback(async () => {
    setState(STATE_LOADING);
    await fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    Sentry.setContext('flow', {
      ..._pick(config, ['id', 'name', 'title', 'revision_key', 'shortcode']),
      shortcode: flowShortcode,
    });
    return () => Sentry.setContext('flow', null);
  }, [config, flowShortcode]);

  return [config, state, reloadFlow];
};

const buildAuthListener = (fetchConfig) => {
  const EVENT_MAP = {
    signedIn: () => fetchConfig(),
    signedOut: () => fetchConfig(),
    default: () => {},
  };
  return (data) => (EVENT_MAP[data.payload.event] ?? EVENT_MAP.default)();
};
